import { Stack, Typography } from "@mui/material";
import { BarCustomLayerProps, BarDatum, ResponsiveBar } from "@nivo/bar";
import {
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React from "react";
import { SURVEY_STATUS_COLORS } from "../../../utils/constants";

type SurveyStatus = "Submitted" | "In Progress" | "Not Started";

interface SurveyDataItem extends BarDatum, Record<SurveyStatus, number> {
  id: string;
}

const SURVEY_KEYS: SurveyStatus[] = ["Submitted", "In Progress", "Not Started"];
const STATUS_COLORS: Record<SurveyStatus, string> = {
  Submitted: SURVEY_STATUS_COLORS.SUBMITTED,
  "In Progress": SURVEY_STATUS_COLORS.IN_PROGRESS,
  "Not Started": SURVEY_STATUS_COLORS.NOT_STARTED,
};
const LABEL_MIN_WIDTH = 60;

const BarLabelsLayer: React.FC<BarCustomLayerProps<SurveyDataItem>> = ({
  bars,
}) => {
  const tokens = useFoundationColorTokens();
  const typography = useTypography();

  return (
    <>
      {bars.map((bar) => {
        const { x, y, width, height, data } = bar;

        if (width < LABEL_MIN_WIDTH) return null;

        const total = SURVEY_KEYS.reduce(
          (sum, key) => sum + (data.data[key] ?? 0),
          0,
        );
        const percentage = data.value
          ? ((data.value / total) * 100).toFixed(2)
          : 0;

        return (
          <g
            key={bar.key}
            transform={`translate(${x + width / 2}, ${y + height / 2})`} // Align label at the center of the bar
            pointerEvents="none"
          >
            <text
              textAnchor="middle"
              dominantBaseline="central"
              style={{ ...typography.b2, fill: tokens.labelOnSurface }}
              y="-10"
            >
              {data.value}
            </text>
            <text
              textAnchor="middle"
              dominantBaseline="central"
              style={{ ...typography.b2, fill: tokens.labelOnSurface }}
              y="10"
            >
              {percentage}%
            </text>
          </g>
        );
      })}
    </>
  );
};

interface SurveyDataProgressBarProps {
  surveyName: string;
  submitted: number;
  inProgress: number;
  notStarted: number;
}

export const SurveyDataProgressBar: React.FC<SurveyDataProgressBarProps> = ({
  surveyName,
  submitted,
  inProgress,
  notStarted,
}) => {
  const data: SurveyDataItem[] = [
    {
      id: surveyName,
      Submitted: submitted,
      "In Progress": inProgress,
      "Not Started": notStarted,
    },
  ];
  const spacing = useSpacing();
  const typography = useTypography();
  const tokens = useFoundationColorTokens();

  return (
    <Stack padding={spacing.spaceXL} gap={spacing.spaceXL}>
      <Stack height={60}>
        <ResponsiveBar<SurveyDataItem>
          data={data}
          keys={SURVEY_KEYS}
          indexBy="id"
          layout="horizontal"
          padding={0}
          colors={({ id }) => STATUS_COLORS[id as SurveyStatus]}
          enableLabel={false}
          enableGridX={false}
          enableGridY={false}
          axisTop={null}
          axisRight={null}
          axisBottom={null}
          axisLeft={null}
          layers={["bars", BarLabelsLayer]}
        />
      </Stack>
      <Stack direction="row" gap={spacing.spaceMD}>
        {SURVEY_KEYS.map((status) => (
          <Stack
            key={status}
            direction="row"
            alignItems="center"
            gap={spacing.spaceXXS}
          >
            <Stack
              width={8}
              height={8}
              borderRadius={"50%"}
              bgcolor={STATUS_COLORS[status]}
            />
            <Typography {...typography.b2} color={tokens.labelSubtle}>
              {status}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
